import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, isSameDay } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import DOMPurify from "dompurify";
import { resolveImageKeys, useAction } from "wasp/client/operations";
import { normalizeSymbol } from "../shared/utils";
import { markdownToHtml } from "../notebook/markdownToHtml";
import { extractS3Keys, applyResolvedUrls } from "../notebook/NotebookEditor";
import type { Trade, TradingSession, TradingRoutine, RoutineCompletion, TradingGoal } from "wasp/entities";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../client/components/ui/dialog";
import { useIsDark } from "../client/components/ui/card-section-header";
import useTranslation from "../client/hooks/useTranslation";
import { useLanguageContext } from "../client/contexts/LanguageContext";
import { calculateMetrics, netPnl } from "../trading-journal/analytics";
import { makeFormat, type LiteFormat } from "../lite/lib/format";
import { HOLIDAYS_BY_DATE, type MarketHoliday } from "./marketHolidays";
import { tradeDayKey } from "./calendarDayKey";
import { useTradingDayStart } from "./useTradingDayStart";
import type { AccountEvent, DayViolationSummary, FundedEvent, PayoutEvent } from "./TradingCalendar";
import type { RuleViolationDay } from "../trading-accounts/accountTypes";
import { AlertCircle, AlertTriangle, CheckCircle2, Circle, Loader2, Trash2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../client/components/ui/tooltip";
import { Button } from "../client/components/ui/button";
import { deleteTrades } from "wasp/client/operations";
import { useToast } from "../client/hooks/use-toast";
import { SessionChart } from "../trading-sessions/SessionChart";

function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "ul", "ol", "li", "h1", "h2", "h3", "blockquote", "a", "img", "span"],
    ALLOWED_ATTR: ["href", "src", "alt", "class", "target", "rel"],
  });
}

function notesToHtml(raw: string): string {
  if (!raw) return "";
  const html = raw.trimStart().startsWith("<") ? raw : markdownToHtml(raw);
  return sanitizeHtml(html);
}

const PAGE_SIZE = 50;

// ─── Helper functions ────────────────────────────────────────────────────────

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const MOOD_EMOJI: Record<string, string> = {
  excellent: "🤩", good: "😊", neutral: "😐", poor: "😕", bad: "😞",
};

const PERF_COLOR: Record<string, string> = {
  excellent: "text-green-500", good: "text-green-400", average: "text-yellow-500", poor: "text-red-500",
};

// ─── Props interface ──────────────────────────────────────────────────────────

interface DayDetailModalProps {
  date: Date | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trades: Trade[];
  sessions: TradingSession[];
  routines: (TradingRoutine & { routineCompletions: RoutineCompletion[] })[];
  goals: TradingGoal[];
  accounts: { id: string; name: string; groupId: string | null; ruleViolations?: Record<string, RuleViolationDay> | null }[];
  groups: { id: string; name: string }[];
  accountEvents?: AccountEvent[];
  fundedEvents?: FundedEvent[];
  payoutEvents?: PayoutEvent[];
  violationsByDate?: Map<string, DayViolationSummary>;
  /**
   * Une synchronisation broker est-elle en cours (ou le dernier cycle a-t-il
   * été bridé) ? Les trades sont écrits UN PAR UN, hors transaction : les
   * totaux de cette modale sont alors PARTIELS.
   *
   * C'est exactement ce qui a produit un ticket support : un utilisateur a lu
   * 166 trades / +636 $ là où la base en contenait 606, a conclu qu'il
   * manquait des comptes, et a relancé un import. Ses données étaient intactes.
   */
  totalsArePartial?: boolean;
  onDeleted?: () => void;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DayDetailModal({
  date, open, onOpenChange, trades, sessions, routines, goals, accounts, groups, accountEvents = [], fundedEvents = [], payoutEvents = [], violationsByDate, totalsArePartial = false, onDeleted,
}: DayDetailModalProps) {
  const t = useTranslation();
  const { language } = useLanguageContext();
  const isDark = useIsDark();
  const locale = language === "fr" ? fr : enUS;
  const fmt = makeFormat(language);
  const [tradePage, setTradePage] = useState(0);
  const [resolvedImageMap, setResolvedImageMap] = useState<Record<string, string>>({});
  const resolveImageKeysFn = useAction(resolveImageKeys);
  // Must be read before the `if (!date)` early return below: hooks cannot be
  // called conditionally.
  const [tradingDayStartUtcHour] = useTradingDayStart();

  const daySessionForEffect = date ? sessions.find((s) => isSameDay(new Date(s.date), date)) ?? null : null;
  const rawSessionNotes = daySessionForEffect?.notes ?? null;

  useEffect(() => {
    if (!rawSessionNotes) return;
    const keys = extractS3Keys(rawSessionNotes);
    if (keys.length === 0) return;
    const missing = keys.filter((k) => !(k in resolvedImageMap));
    if (missing.length === 0) return;
    let cancelled = false;
    resolveImageKeysFn({ s3Keys: missing })
      .then(({ resolved }) => {
        if (cancelled) return;
        setResolvedImageMap((prev) => ({ ...prev, ...resolved }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [rawSessionNotes, resolvedImageMap]);

  if (!date) return null;

  // Trades follow the *trading* day (shifted by the user's start hour, in UTC),
  // exactly like the calendar cells. Filtering with isSameDay() here instead
  // used the browser-local calendar day and disagreed with the cell the user
  // just clicked, so a cell and its own detail popup showed different totals.
  // Sessions/routines/goals below intentionally stay on the plain calendar day:
  // only trades belong to a trading session.
  //
  // Keyed with format() (local), not utcDateKey: the grid keys its cells with
  // format(day, "yyyy-MM-dd") and `date` IS the clicked cell's Date object.
  // Reading it as UTC would shift the key for users west of UTC.
  const selectedDayKey = format(date, "yyyy-MM-dd");
  const dayTrades = trades.filter(
    (tr) => tradeDayKey(tr.date, tradingDayStartUtcHour) === selectedDayKey
  );
  const daySession = sessions.find((s) => isSameDay(new Date(s.date), date)) ?? null;
  const activeRoutines = routines.filter((r) => r.isActive);
  const dayGoalDeadlines = goals.filter(
    (g) => !g.isCompleted && g.deadline && isSameDay(new Date(g.deadline), date)
  );
  const dayAccountEvents = accountEvents.filter((e) => isSameDay(new Date(e.date), date));
  const dayFundedAccountNames = new Set(
    fundedEvents.filter((e) => isSameDay(new Date(e.date), date)).map((e) => e.accountName)
  );
  const dayPayoutAccountNames = new Set(
    payoutEvents.filter((e) => isSameDay(new Date(e.date), date)).map((e) => e.accountName)
  );

  const totalPages = Math.ceil(dayTrades.length / PAGE_SIZE);
  const formattedDate = format(date, "EEEE d MMMM yyyy", { locale });
  const dayKey = format(date, "yyyy-MM-dd");
  const dayHolidays = HOLIDAYS_BY_DATE.get(dayKey) ?? [];

  // Window for the session chart: prefer the real timed session, else derive
  // from the day's trades (earliest entry → latest exit). Null window → chart
  // renders nothing.
  const chartWindow: { startedAt: Date | null; endedAt: Date | null } = (() => {
    if (daySession?.startedAt && daySession?.endedAt) {
      return { startedAt: new Date(daySession.startedAt), endedAt: new Date(daySession.endedAt) };
    }
    if (dayTrades.length > 0) {
      let minStart = Infinity;
      let maxEnd = -Infinity;
      for (const tr of dayTrades) {
        const entry = new Date(tr.date).getTime();
        const exit = tr.exitDate ? new Date(tr.exitDate).getTime() : entry;
        if (entry < minStart) minStart = entry;
        if (exit > maxEnd) maxEnd = exit;
      }
      // Widen a zero-length window so the chart has a range to zoom to.
      if (minStart === maxEnd) {
        minStart -= 30 * 60_000;
        maxEnd += 30 * 60_000;
      }
      return { startedAt: new Date(minStart), endedAt: new Date(maxEnd) };
    }
    return { startedAt: null, endedAt: null };
  })();

  const sessionNotes = daySession?.notes ? applyResolvedUrls(daySession.notes, resolvedImageMap) : null;
  // Durée affichée : celle de la session minutée, sinon la fenêtre des trades.
  const durationMin = daySession?.actualDuration
    ?? (chartWindow.startedAt && chartWindow.endedAt && dayTrades.length > 1
      ? Math.round((chartWindow.endedAt.getTime() - chartWindow.startedAt.getTime()) / 60_000)
      : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        {/* Titre pour les lecteurs d'écran : la date visible vit dans l'en-tête de la journée. */}
        <DialogHeader className="sr-only">
          <DialogTitle className="capitalize">{formattedDate}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* TOTAUX PARTIELS — affiché AVANT les chiffres, pas après : un
              utilisateur qui lit d'abord un total faux ne revient pas sur un
              avertissement placé plus bas. C'est le correctif du ticket
              support récurrent « il manque des comptes / des trades ». */}
          {totalsArePartial && (
            <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10 p-3">
              <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[hsl(var(--warning))]" />
              <div className="text-xs">
                <p className="font-medium">{t.tradovate.syncRunningTitle}</p>
                <p className="text-muted-foreground">{t.tradovate.syncRunningHint}</p>
              </div>
            </div>
          )}
          {/* L'essentiel d'abord : la journée en un coup d'œil, pensée pour
              tenir dans une capture d'écran partageable. */}
          <DayHero
            dateLabel={formattedDate}
            trades={dayTrades}
            session={daySession}
            durationMin={durationMin}
            fmt={fmt}
            t={t}
          />
          <AccountsSection
            trades={dayTrades}
            events={dayAccountEvents}
            accounts={accounts}
            groups={groups}
            dayKey={dayKey}
            fundedAccountNames={dayFundedAccountNames}
            payoutAccountNames={dayPayoutAccountNames}
            fmt={fmt}
            t={t}
          />
          <SessionChart
            session={chartWindow}
            trades={dayTrades}
            accounts={accounts}
            isDark={isDark}
          />
          {sessionNotes && <SessionNotesSection notes={sessionNotes} t={t} isDark={isDark} />}
          <TradesSection
            trades={dayTrades}
            accounts={accounts}
            page={tradePage}
            totalPages={totalPages}
            onPageChange={setTradePage}
            fmt={fmt}
            t={t}
            isDark={isDark}
            onDeleted={onDeleted}
            formattedDate={formattedDate}
            onClose={() => onOpenChange(false)}
          />
          <RoutinesSection routines={activeRoutines} date={date} t={t} />
          <GoalDeadlinesSection goals={dayGoalDeadlines} t={t} isDark={isDark} />
          {dayHolidays.length > 0 && (
            <MarketHolidaysSection holidays={dayHolidays} t={t} isDark={isDark} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

type T = ReturnType<typeof useTranslation>;
type Fmt = LiteFormat;
type DayAccount = { id: string; name: string; groupId: string | null; ruleViolations?: Record<string, RuleViolationDay> | null };

function SectionTitle({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-2.5">
      <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">{children}</p>
      {right}
    </div>
  );
}

const pnlText = (v: number) => (v > 0 ? "text-pnlPositive" : v < 0 ? "text-pnlNegative" : "text-muted-foreground");

function PnlBadge({ value, fmt }: { value: number; fmt: Fmt }) {
  return <span className={`font-data font-semibold ${pnlText(value)}`}>{fmt.money(value, { decimals: true })}</span>;
}

/**
 * En-tête de la journée : P&L net en très gros, courbe intraday, et les
 * chiffres clés. C'est le bloc qu'on partage en capture d'écran : il porte
 * donc la date et le logo, et se suffit à lui-même.
 */
function DayHero({
  dateLabel, trades, session, durationMin, fmt, t,
}: {
  dateLabel: string;
  trades: Trade[];
  session: TradingSession | null;
  durationMin: number | null;
  fmt: Fmt;
  t: T;
}) {
  const m = useMemo(() => calculateMetrics(trades), [trades]);
  const total = trades.reduce((sum, tr) => sum + netPnl(tr), 0);
  const tone = total > 0 ? "positive" : total < 0 ? "negative" : "neutral";
  const toneVar = tone === "negative" ? "--edgely-pnl-negative" : "--edgely-pnl-positive";
  const glow = tone === "neutral"
    ? undefined
    : `radial-gradient(120% 90% at 0% 0%, hsl(var(${toneVar}) / 0.16), transparent 60%)`;

  const moodLabel: Record<string, string> = {
    excellent: t.session.moodExcellent, good: t.session.moodGood, neutral: t.session.moodNeutral,
    poor: t.session.moodPoor, bad: t.session.moodBad,
  };
  const perfLabel: Record<string, string> = {
    excellent: t.session.perfExcellent, good: t.session.perfGood, average: t.session.perfAverage, poor: t.session.perfPoor,
  };

  const chips: React.ReactNode[] = [];
  if (trades.length > 0) chips.push(`${trades.length} ${t.dashboard.dayDetail.tradesCount}`);
  if (durationMin != null && durationMin > 0) chips.push(formatDuration(durationMin));
  if (session?.mood) chips.push(`${MOOD_EMOJI[session.mood] ?? ""} ${moodLabel[session.mood] ?? session.mood}`);
  if (session?.performance) {
    chips.push(<span className={PERF_COLOR[session.performance] ?? ""}>{perfLabel[session.performance] ?? session.performance}</span>);
  }

  const stats: { label: string; value: string; className?: string }[] = trades.length === 0 ? [] : [
    { label: t.dashboard.dayDetail.winRate, value: fmt.pct(m.winRate / 100) },
    { label: t.dashboard.dayDetail.profitFactor, value: m.averageLoss > 0 ? fmt.num(m.profitFactor) : "∞" },
    { label: t.dashboard.dayDetail.avgWin, value: m.winningTrades ? fmt.money(m.averageWin, { decimals: true }) : "-", className: m.winningTrades ? "text-pnlPositive" : undefined },
    { label: t.dashboard.dayDetail.avgLoss, value: m.losingTrades ? fmt.money(-m.averageLoss, { decimals: true }) : "-", className: m.losingTrades ? "text-pnlNegative" : undefined },
    { label: t.dashboard.dayDetail.bestTrade, value: m.winningTrades ? fmt.money(m.largestWin, { decimals: true }) : "-", className: m.winningTrades ? "text-pnlPositive" : undefined },
    { label: t.dashboard.dayDetail.worstTrade, value: m.losingTrades ? fmt.money(m.largestLoss, { decimals: true }) : "-", className: m.losingTrades ? "text-pnlNegative" : undefined },
  ];

  return (
    <section
      className="relative overflow-hidden rounded-xl border border-border/60 bg-card"
      style={glow ? { backgroundImage: glow } : undefined}
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <p className="text-xs font-medium capitalize text-muted-foreground">{dateLabel}</p>
          <span className="font-logo font-bold text-sm tracking-tight text-foreground/80 shrink-0 mr-6">
            edgely<span className="text-[#00C9A7]">.</span>
          </span>
        </div>

        {trades.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">{t.dashboard.dayDetail.noTrades}</p>
        ) : (
          <div className="mt-3 grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:items-end">
            <div className="min-w-0">
              <p className={`font-data font-semibold tracking-tight leading-none text-4xl sm:text-5xl ${pnlText(total)}`}>
                {fmt.money(total, { decimals: true })}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                {t.dashboard.dayDetail.winsLosses(m.winningTrades, m.losingTrades)}
              </p>
            </div>
            <IntradayCurve trades={trades} toneVar={toneVar} label={t.dashboard.dayDetail.intradayCurve} />
          </div>
        )}

        {chips.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {chips.map((c, i) => (
              <span key={i} className="rounded-full border border-border/60 bg-background/40 px-2.5 py-1 text-[11px] text-foreground/90">
                {c}
              </span>
            ))}
          </div>
        )}
      </div>

      {stats.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px border-t border-border/60 bg-border/60">
          {stats.map((st) => (
            <div key={st.label} className="bg-card px-5 py-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{st.label}</p>
              <p className={`mt-1 font-data text-base font-semibold ${st.className ?? "text-foreground"}`}>{st.value}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** Cumul du P&L net trade après trade, dans l'ordre des sorties. */
function IntradayCurve({ trades, toneVar, label }: { trades: Trade[]; toneVar: string; label: string }) {
  const id = useMemo(() => `dd-curve-${Math.random().toString(36).slice(2, 8)}`, []);
  const points = useMemo(() => {
    const sorted = [...trades].sort(
      (a, b) => new Date(a.exitDate ?? a.date).getTime() - new Date(b.exitDate ?? b.date).getTime(),
    );
    let cum = 0;
    return [0, ...sorted.map((tr) => (cum += netPnl(tr)))];
  }, [trades]);
  if (points.length < 3) return null;

  const W = 300, H = 72, pad = 3;
  const min = Math.min(...points), max = Math.max(...points);
  const span = max - min || 1;
  const x = (i: number) => (i / (points.length - 1)) * W;
  const y = (v: number) => pad + (1 - (v - min) / span) * (H - pad * 2);
  const line = points.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const stroke = `hsl(var(${toneVar}))`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-[72px] w-full" role="img" aria-label={label}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.28" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      {min < 0 && max > 0 && (
        <line x1="0" x2={W} y1={y(0)} y2={y(0)} stroke="currentColor" className="text-muted-foreground/30" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
      )}
      <path d={`${line} L${W},${H} L0,${H} Z`} fill={`url(#${id})`} />
      <path d={line} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/**
 * Comptes de la journée : P&L par compte (avec alertes de règles, funded,
 * payout), puis les comptes créés et cramés ce jour-là.
 */
function AccountsSection({
  trades, events, accounts, groups, dayKey, fundedAccountNames, payoutAccountNames, fmt, t,
}: {
  trades: Trade[];
  events: AccountEvent[];
  accounts: DayAccount[];
  groups: { id: string; name: string }[];
  dayKey: string;
  fundedAccountNames: Set<string>;
  payoutAccountNames: Set<string>;
  fmt: Fmt;
  t: T;
}) {
  const accountMap = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const groupMap = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);

  if (trades.length === 0 && events.length === 0) return null;

  const byAccount = new Map<string, { name: string; groupName: string | null; pnl: number; fees: number; count: number; accountId: string | null }>();
  for (const tr of trades) {
    const key = tr.accountId ?? "__none__";
    const acc = tr.accountId ? accountMap.get(tr.accountId) : null;
    const grp = acc?.groupId ? groupMap.get(acc.groupId) : null;
    if (!byAccount.has(key)) {
      byAccount.set(key, { name: acc?.name ?? "-", groupName: grp?.name ?? null, pnl: 0, fees: 0, count: 0, accountId: tr.accountId ?? null });
    }
    const entry = byAccount.get(key)!;
    entry.pnl += netPnl(tr);
    entry.fees += (tr.commission ?? 0) + (tr.swaps ?? 0);
    entry.count += 1;
  }
  const rows = Array.from(byAccount.values()).sort((a, b) => b.pnl - a.pnl);
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.pnl)));

  // Build a map of accountId → violation types for this specific day
  const accountViolationMap = new Map<string, RuleViolationDay>();
  for (const acc of accounts) {
    const dayViolation = acc.ruleViolations?.[dayKey];
    if (dayViolation?.types?.length) {
      accountViolationMap.set(acc.id, dayViolation);
    }
  }

  return (
    <div>
      <SectionTitle right={rows.length > 0 ? <span className="text-[11px] text-muted-foreground">{rows.length}</span> : undefined}>
        {t.dashboard.dayDetail.accountsTraded}
      </SectionTitle>

      {rows.length > 0 && (
        <div className="rounded-lg border border-border/60 divide-y divide-border/60">
          {rows.map((entry, i) => {
            const dayViolation = entry.accountId ? accountViolationMap.get(entry.accountId) : undefined;
            return (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                <ViolationIcon dayViolation={dayViolation} t={t} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-sm">
                    <span className="truncate font-medium">{entry.groupName ? `${entry.groupName} / ` : ""}{entry.name}</span>
                    {fundedAccountNames.has(entry.name) && (
                      <span className="text-[11px] leading-none shrink-0" title={t.dashboard.calendar.accountFunded}>🏆</span>
                    )}
                    {payoutAccountNames.has(entry.name) && (
                      <span className="text-[11px] leading-none shrink-0" title={t.dashboard.calendar.calendarPayout}>💰</span>
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="h-1 flex-1 rounded-full bg-muted/50 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${entry.pnl >= 0 ? "bg-pnlPositive" : "bg-pnlNegative"}`}
                        style={{ width: `${(Math.abs(entry.pnl) / maxAbs) * 100}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {entry.count} {t.dashboard.dayDetail.tradesCount}
                      {entry.fees !== 0 && ` · ${t.dashboard.dayDetail.fees} ${fmt.num(entry.fees)}`}
                    </span>
                  </div>
                </div>
                <span className="shrink-0 text-sm"><PnlBadge value={entry.pnl} fmt={fmt} /></span>
              </div>
            );
          })}
        </div>
      )}

      {events.length > 0 && (
        <div className={`flex flex-wrap gap-1.5 ${rows.length > 0 ? "mt-2.5" : ""}`}>
          {events.map((event, i) => {
            const created = event.type === "created";
            return (
              <span
                key={i}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${
                  created
                    ? "border-pnlPositive/30 bg-pnlPositive/10"
                    : "border-pnlNegative/30 bg-pnlNegative/10"
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${created ? "bg-pnlPositive" : "bg-pnlNegative"}`} />
                <span className="font-medium">{event.accountName}</span>
                <span className={created ? "text-pnlPositive" : "text-pnlNegative"}>
                  {created ? t.dashboard.calendar.accountCreated : t.dashboard.calendar.accountCrashed}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ViolationIcon({ dayViolation, t }: { dayViolation: RuleViolationDay | undefined; t: T }) {
  const types = dayViolation?.types ?? [];
  const isViolation = types.some((tp) => tp.endsWith("_violation"));
  const isWarning = !isViolation && types.some((tp) => tp.endsWith("_warning"));
  if (!isViolation && !isWarning) return null;

  const violationLabels = types.map((tp) => {
    if (tp === "daily_loss_violation") return t.tradingAccounts.violationDailyLoss;
    if (tp === "daily_loss_warning") return t.tradingAccounts.warningDailyLoss;
    if (tp === "consistency_violation") return t.tradingAccounts.violationConsistency;
    if (tp === "consistency_warning") return t.tradingAccounts.warningConsistency;
    return tp;
  }).filter((v, i2, a) => a.indexOf(v) === i2);

  // Le verdict seul ("dépassé") ne permet pas de savoir sur quelle
  // limite il a été rendu. On affiche les deux chiffres sous le
  // libellé quand le serveur les fournit.
  const dailyLossDetail =
    types.some((tp) => tp.startsWith("daily_loss")) &&
    dayViolation?.maxDailyLossAllowed != null &&
    dayViolation.dailyPnl != null
      ? t.dashboard.calendar.violations.tooltipDailyLoss
          .replace("{loss}", Math.abs(dayViolation.dailyPnl).toFixed(2))
          .replace("{max}", dayViolation.maxDailyLossAllowed.toFixed(2))
      : null;

  // POURCENTAGE AFFICHÉ = CELUI DU VERDICT.
  //
  // Ce détail recalculait sa propre part, `dailyPnl / totalProfit`,
  // alors que le moteur compare à `consistencyBase` (le profit target
  // du palier). Les deux chiffres pouvaient donc se contredire : un jour
  // déclaré « violé » à 34 % du target s'affichait ici à 100 % du profit
  // réalisé. Et sa garde `totalProfit > 0` le faisait taire exactement
  // dans le cas qui a motivé ce correctif, où `totalProfit` vaut 0 parce
  // que le solde broker est en retard sur les trades.
  //
  // On lit maintenant `daySharePercent`, la part que le moteur a
  // réellement comparée au seuil. Repli sur l'ancien calcul quand le
  // serveur ne l'envoie pas encore (payload antérieur au correctif).
  const consistencyShare =
    dayViolation?.daySharePercent != null &&
    Number.isFinite(dayViolation.daySharePercent)
      ? dayViolation.daySharePercent
      : dayViolation?.totalProfit != null && dayViolation.totalProfit > 0 && dayViolation.dailyPnl != null
        ? (dayViolation.dailyPnl / dayViolation.totalProfit) * 100
        : null;

  const consistencyDetail =
    types.some((tp) => tp.startsWith("consistency")) &&
    dayViolation?.consistencyRulePercent != null &&
    consistencyShare != null
      ? t.dashboard.calendar.violations.tooltipConsistency
          .replace("{pct}", consistencyShare.toFixed(1))
          .replace("{max}", String(dayViolation.consistencyRulePercent))
      : null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="shrink-0 cursor-default">
            {isViolation
              ? <AlertCircle className="h-4 w-4 text-red-500" />
              : <AlertTriangle className="h-4 w-4 text-yellow-500" />
            }
          </span>
        </TooltipTrigger>
        {/*
          L'icône est en bord gauche du modal : un tooltip centré
          sur elle (side="top") sortait du conteneur. On l'ouvre
          vers la droite ; le portail et le collisionPadding
          définis dans TooltipContent font le reste.
        */}
        <TooltipContent
          side="right"
          align="start"
          className="max-w-[260px] whitespace-normal"
        >
          <ul className="space-y-1">
            {violationLabels.map((label, idx) => (
              <li key={idx}>{label}</li>
            ))}
          </ul>
          {(dailyLossDetail || consistencyDetail) && (
            <div className="mt-1.5 pt-1.5 border-t border-border/50 space-y-0.5 text-[11px] opacity-80">
              {dailyLossDetail && <div>{dailyLossDetail}</div>}
              {consistencyDetail && <div>{consistencyDetail}</div>}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function SessionNotesSection({ notes, t, isDark }: { notes: string; t: T; isDark: boolean }) {
  return (
    <div>
      <SectionTitle>{t.dashboard.dayDetail.sessionNotes}</SectionTitle>
      <div
        className={`text-xs rounded-md p-3 prose prose-sm max-w-none ${isDark ? "bg-muted/30 prose-invert" : "bg-muted/50"}`}
        dangerouslySetInnerHTML={{ __html: notesToHtml(notes) }}
      />
    </div>
  );
}

function TradesSection({
  trades, accounts, page, totalPages, onPageChange, fmt, t, isDark, onDeleted, formattedDate, onClose,
}: {
  trades: Trade[];
  accounts: DayAccount[];
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  fmt: Fmt;
  t: T;
  isDark: boolean;
  onDeleted?: () => void;
  formattedDate: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const accountMap = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);

  const deleteTradesFn = useAction(deleteTrades);
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAll = async () => {
    if (trades.length === 0) return;
    setIsDeleting(true);
    try {
      await deleteTradesFn({ ids: trades.map((tr) => tr.id) });
      toast({ title: t.dashboard.dayDetail.deleteAllTradesSuccess });
      setDeleteDialogOpen(false);
      onDeleted?.();
    } catch (_) {
      toast({ title: t.common.error, variant: "destructive" });
    } finally {
      setIsDeleting(false);
    }
  };

  if (trades.length === 0) return null;

  const pageTrades = trades.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div>
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.dashboard.dayDetail.deleteAllTradesTitle}</DialogTitle>
            <DialogDescription>
              {t.dashboard.dayDetail.deleteAllTradesConfirm
                .replace("{count}", String(trades.length))
                .replace("{date}", formattedDate)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={isDeleting} onClick={() => setDeleteDialogOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAll}
              disabled={isDeleting}
            >
              {isDeleting ? <Loader2 className="h-3 w-3 animate-spin" /> : t.dashboard.dayDetail.deleteAllTrades}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SectionTitle
        right={onDeleted && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-[11px] gap-1 text-muted-foreground hover:text-destructive"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <Trash2 className="h-3 w-3" />
            {t.dashboard.dayDetail.deleteAllTrades}
          </Button>
        )}
      >
        {trades.length} {t.dashboard.dayDetail.tradesCount}
      </SectionTitle>

      {/* Individual trades table — paginated */}
      <div className="rounded-md border overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className={`border-b ${isDark ? "bg-muted/30" : "bg-muted/50"}`}>
              <th className="text-left p-2 font-semibold">{t.dashboard.dayDetail.symbol}</th>
              <th className="text-left p-2 font-semibold">{t.dashboard.dayDetail.direction}</th>
              <th className="text-right p-2 font-semibold">{t.dashboard.dayDetail.pnl}</th>
              <th className="text-left p-2 font-semibold hidden sm:table-cell">{t.dashboard.dayDetail.account}</th>
            </tr>
          </thead>
          <tbody>
            {pageTrades.map((tr) => {
              const acc = tr.accountId ? accountMap.get(tr.accountId) : null;
              const pnl = netPnl(tr);
              return (
                <tr
                  key={tr.id}
                  className="border-b last:border-0 cursor-pointer hover:bg-accent/40 transition-colors"
                  onClick={() => {
                    onClose();
                    navigate(`/trade/${tr.id}`);
                  }}
                >
                  <td className="p-2 font-mono font-semibold">{normalizeSymbol(tr.symbol)}</td>
                  <td className="p-2">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      tr.type === "long"
                        ? "bg-green-500/15 text-green-500"
                        : "bg-red-500/15 text-red-500"
                    }`}>
                      {tr.type.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-2 text-right"><PnlBadge value={pnl} fmt={fmt} /></td>
                  <td className="p-2 text-muted-foreground hidden sm:table-cell truncate max-w-[80px]">
                    {acc?.name ?? "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => onPageChange(page - 1)}
            className="text-xs px-2 py-1 rounded border disabled:opacity-30 hover:bg-accent transition-colors"
          >
            ‹
          </button>
          <span className="text-xs text-muted-foreground">
            {t.dashboard.dayDetail.page} {page + 1} {t.dashboard.dayDetail.of} {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={() => onPageChange(page + 1)}
            className="text-xs px-2 py-1 rounded border disabled:opacity-30 hover:bg-accent transition-colors"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}

/** Routines du jour : discrètes, en bas, une puce par routine. */
function RoutinesSection({
  routines, date, t,
}: {
  routines: (TradingRoutine & { routineCompletions: RoutineCompletion[] })[];
  date: Date;
  t: T;
}) {
  if (routines.length === 0) return null;

  const isDone = (r: (typeof routines)[number]) => r.routineCompletions.some((c) => isSameDay(new Date(c.date), date));
  const doneCount = routines.filter(isDone).length;
  const allDone = doneCount === routines.length;

  return (
    <div>
      <SectionTitle
        right={
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${allDone ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
            {doneCount}/{routines.length}
          </span>
        }
      >
        {t.dashboard.dayDetail.routines}
      </SectionTitle>
      <div className="flex flex-wrap gap-1.5">
        {routines.map((r) => {
          const done = isDone(r);
          return (
            <span
              key={r.id}
              title={done ? t.dashboard.dayDetail.completed : t.dashboard.dayDetail.notCompleted}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                done ? "border-primary/30 bg-primary/10 text-foreground" : "border-border/60 text-muted-foreground"
              }`}
            >
              {done ? <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> : <Circle className="h-3.5 w-3.5" />}
              {r.name}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function GoalDeadlinesSection({
  goals, t, isDark,
}: {
  goals: TradingGoal[];
  t: ReturnType<typeof useTranslation>;
  isDark: boolean;
}) {
  if (goals.length === 0) return null;

  return (
    <div>
      <SectionTitle>{t.dashboard.dayDetail.goalDeadlines}</SectionTitle>
      <div className="grid grid-cols-1 gap-2">
        {goals.map((g) => {
          const progress = g.targetValue > 0 ? Math.min((g.currentValue / g.targetValue) * 100, 100) : 0;
          const isComplete = progress >= 100;
          return (
            <div
              key={g.id}
              className={`rounded-lg px-3 py-2.5 border ${
                isComplete
                  ? isDark ? "bg-green-950/40 border-green-500/30" : "bg-green-50 border-green-200"
                  : isDark ? "bg-muted/20 border-border/40" : "bg-muted/30 border-border/40"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg leading-none shrink-0">{isComplete ? "🏆" : "🎯"}</span>
                  <span className="text-sm font-medium truncate">{g.title}</span>
                </div>
                <span className={`text-sm font-bold shrink-0 ml-2 ${isComplete ? "text-green-400" : "text-primary"}`}>
                  {progress.toFixed(0)}%
                </span>
              </div>
              <div className="w-full h-2 bg-muted/50 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${isComplete ? "bg-green-400" : "bg-primary"}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-muted-foreground">{g.currentValue}</span>
                <span className="text-[10px] text-muted-foreground">{g.targetValue}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MarketHolidaysSection({
  holidays, t, isDark,
}: {
  holidays: MarketHoliday[];
  t: ReturnType<typeof useTranslation>;
  isDark: boolean;
}) {
  const usHolidays = holidays.filter(h => h.market === 'US');
  const euHolidays = holidays.filter(h => h.market === 'EU');

  return (
    <div>
      <SectionTitle>{t.dashboard.dayDetail.marketHolidays}</SectionTitle>
      <div className="space-y-1.5">
        {usHolidays.map(h => (
          <div key={`US-${h.date}-${h.label}`} className="flex items-center gap-2 text-sm">
            <span className="text-base leading-none">🇺🇸</span>
            <span className="flex-1">{h.label}</span>
            <span className="text-xs text-muted-foreground shrink-0">NYSE {t.dashboard.dayDetail.marketClosed}</span>
          </div>
        ))}
        {euHolidays.map(h => (
          <div key={`EU-${h.date}-${h.label}`} className="flex items-center gap-2 text-sm">
            <span className="text-base leading-none">🇪🇺</span>
            <span className="flex-1">{h.label}</span>
            <span className="text-xs text-muted-foreground shrink-0">Euronext {t.dashboard.dayDetail.marketClosed}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
